class ProductCountdown {
  constructor(element) {
    this.element = element;
    this.sectionId = element.dataset.sectionId;
    this.countdownFormat = element.dataset.countdownFormat || 'separated';
    this.countdownFormatMobile = element.dataset.countdownFormatMobile || this.countdownFormat;
    this.isComingSoon = element.dataset.isComingSoon === 'true';
    
    // Try banner countdown selector first
    this.countdownElement = element.querySelector(`#Countdown-${this.sectionId} .countdown-banner__countdown-text`);
    
    // Fallback: try product page countdown selector
    if (!this.countdownElement) {
      this.countdownElement = element.querySelector(`#ProductCountdown-${this.sectionId} .product__countdown-text`);
    }
    
    // Fallback: try generic selectors
    if (!this.countdownElement) {
      this.countdownElement = element.querySelector('.countdown-banner__countdown-text');
    }
    if (!this.countdownElement) {
      this.countdownElement = element.querySelector('.product__countdown-text');
    }
    
    this.buttonElement = element.querySelector(`#CountdownButton-${this.sectionId}`);
    
    // For product pages, check if this is a product countdown
    this.isProductPage = element.id && element.id.startsWith('ProductCountdown-');
    
    if (!this.countdownElement || !this.isComingSoon) {
      return;
    }

    // Parse release date from data attributes
    // Prefer timestamp if available (more reliable), otherwise parse date string
    if (element.dataset.releaseTimestamp) {
      const timestamp = parseInt(element.dataset.releaseTimestamp, 10);
      this.releaseDate = new Date(timestamp * 1000);
    } else if (element.dataset.releaseDate) {
      this.releaseDate = new Date(element.dataset.releaseDate);
    } else {
      return;
    }

    // Validate date
    if (isNaN(this.releaseDate.getTime())) {
      console.error('Invalid release date for countdown banner:', element.dataset.releaseDate);
      return;
    }

    this.intervalId = null;
    this.init();
  }

  init() {
    this.updateCountdown();
    this.handleSpacing();
    this.intervalId = setInterval(() => {
      this.updateCountdown();
    }, 1000);
  }

  handleSpacing() {
    // Check if countdown/available text and button are in the same position
    const countdownEl = this.element.querySelector(`#Countdown-${this.sectionId}`);
    const availableEl = this.element.querySelector('.countdown-banner__available');
    const buttonWrapper = this.element.querySelector('.countdown-banner__button-wrapper');
    
    if (!buttonWrapper) return;
    
    // Check countdown position
    if (countdownEl) {
      const countdownPosition = Array.from(countdownEl.classList).find(cls => cls.startsWith('countdown-banner__countdown--'));
      const buttonPosition = Array.from(buttonWrapper.classList).find(cls => cls.startsWith('countdown-banner__button-wrapper--'));
      
      if (countdownPosition && buttonPosition) {
        const countdownPos = countdownPosition.replace('countdown-banner__countdown--', '');
        const buttonPos = buttonPosition.replace('countdown-banner__button-wrapper--', '');
        
        if (countdownPos === buttonPos) {
          buttonWrapper.setAttribute('data-same-position', 'true');
          return;
        }
      }
    }
    
    // Check available text position
    if (availableEl) {
      const availablePosition = Array.from(availableEl.classList).find(cls => cls.startsWith('countdown-banner__available--'));
      const buttonPosition = Array.from(buttonWrapper.classList).find(cls => cls.startsWith('countdown-banner__button-wrapper--'));
      
      if (availablePosition && buttonPosition) {
        const availablePos = availablePosition.replace('countdown-banner__available--', '');
        const buttonPos = buttonPosition.replace('countdown-banner__button-wrapper--', '');
        
        if (availablePos === buttonPos) {
          buttonWrapper.setAttribute('data-same-position', 'true');
        }
      }
    }
  }

  updateCountdown() {
    const now = new Date();
    const diff = this.releaseDate.getTime() - now.getTime();

    if (diff <= 0) {
      // Countdown expired
      this.handleExpiration();
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    const formatted = this.formatCountdown(days, hours, minutes, seconds);
    if (this.countdownElement) {
      this.countdownElement.innerHTML = formatted;
    }
  }

  formatCountdown(days, hours, minutes, seconds) {
    const pad = (num) => String(num).padStart(2, '0');
    const showSeconds = this.shouldShowSeconds(days, hours);
    
    // Use mobile format if viewport is mobile (<= 749px)
    const isMobile = window.innerWidth <= 749;
    const format = isMobile ? this.countdownFormatMobile : this.countdownFormat;

    switch (format) {
      case 'readable':
        return this.formatReadable(days, hours, minutes, false);
      
      case 'readable_with_seconds':
        return this.formatReadable(days, hours, minutes, true, seconds);
      
      case 'clock':
        return `${pad(days)}:${pad(hours)}:${pad(minutes)}`;
      
      case 'clock_with_seconds':
        return `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
      
      case 'separated':
        if (this.isProductPage) {
          return `<span><span class="product__countdown-number">${pad(days)}</span><span class="product__countdown-label">DAYS</span></span> <span class="product__countdown-separator">|</span> <span><span class="product__countdown-number">${pad(hours)}</span><span class="product__countdown-label">HOURS</span></span> <span class="product__countdown-separator">|</span> <span><span class="product__countdown-number">${pad(minutes)}</span><span class="product__countdown-label">MINUTES</span></span>`;
        }
        return `${pad(days)} <span class="countdown-banner__countdown-separator">|</span> ${pad(hours)} <span class="countdown-banner__countdown-separator">|</span> ${pad(minutes)}`;
      
      case 'separated_with_seconds':
        if (this.isProductPage) {
          return `<span><span class="product__countdown-number">${pad(days)}</span><span class="product__countdown-label">DAYS</span></span> <span class="product__countdown-separator">|</span> <span><span class="product__countdown-number">${pad(hours)}</span><span class="product__countdown-label">HOURS</span></span> <span class="product__countdown-separator">|</span> <span><span class="product__countdown-number">${pad(minutes)}</span><span class="product__countdown-label">MINUTES</span></span> <span class="product__countdown-separator">|</span> <span><span class="product__countdown-number">${pad(seconds)}</span><span class="product__countdown-label">SECONDS</span></span>`;
        }
        return `${pad(days)} <span class="countdown-banner__countdown-separator">|</span> ${pad(hours)} <span class="countdown-banner__countdown-separator">|</span> ${pad(minutes)} <span class="countdown-banner__countdown-separator">|</span> ${pad(seconds)}`;
      
      case 'smart':
        if (this.isProductPage) {
          if (showSeconds) {
            return `<span><span class="product__countdown-number">${pad(days)}</span><span class="product__countdown-label">DAYS</span></span> <span class="product__countdown-separator">|</span> <span><span class="product__countdown-number">${pad(hours)}</span><span class="product__countdown-label">HOURS</span></span> <span class="product__countdown-separator">|</span> <span><span class="product__countdown-number">${pad(minutes)}</span><span class="product__countdown-label">MINUTES</span></span> <span class="product__countdown-separator">|</span> <span><span class="product__countdown-number">${pad(seconds)}</span><span class="product__countdown-label">SECONDS</span></span>`;
          } else {
            return `<span><span class="product__countdown-number">${pad(days)}</span><span class="product__countdown-label">DAYS</span></span> <span class="product__countdown-separator">|</span> <span><span class="product__countdown-number">${pad(hours)}</span><span class="product__countdown-label">HOURS</span></span> <span class="product__countdown-separator">|</span> <span><span class="product__countdown-number">${pad(minutes)}</span><span class="product__countdown-label">MINUTES</span></span>`;
          }
        }
        if (showSeconds) {
          return `${pad(days)} <span class="countdown-banner__countdown-separator">|</span> ${pad(hours)} <span class="countdown-banner__countdown-separator">|</span> ${pad(minutes)} <span class="countdown-banner__countdown-separator">|</span> ${pad(seconds)}`;
        } else {
          return `${pad(days)} <span class="countdown-banner__countdown-separator">|</span> ${pad(hours)} <span class="countdown-banner__countdown-separator">|</span> ${pad(minutes)}`;
        }
      
      case 'card':
        return this.formatCard(days, hours, minutes, seconds);
      
      case 'card_with_seconds':
        return this.formatCard(days, hours, minutes, seconds, true);
      
      default:
        return `${pad(days)} <span class="countdown-banner__countdown-separator">|</span> ${pad(hours)} <span class="countdown-banner__countdown-separator">|</span> ${pad(minutes)}`;
    }
  }

  formatCard(days, hours, minutes, seconds, alwaysShowSeconds = false) {
    const pad = (num) => String(num).padStart(2, '0');
    const showSeconds = alwaysShowSeconds || (days === 0 && hours < 24);
    
    const createCardUnit = (value, label) => {
      const padded = pad(value);
      return `
        <div class="countdown-banner__card-unit">
          <div class="countdown-banner__card-digits">
            <div class="countdown-banner__card-digit">${padded[0]}</div>
            <div class="countdown-banner__card-digit">${padded[1]}</div>
          </div>
          <div class="countdown-banner__card-label">${label}</div>
        </div>
      `;
    };
    
    let html = '<div class="countdown-banner__card-view">';
    html += createCardUnit(days, 'Days');
    html += createCardUnit(hours, 'Hours');
    html += createCardUnit(minutes, 'Minutes');
    
    if (showSeconds) {
      html += createCardUnit(seconds, 'Seconds');
    }
    
    html += '</div>';
    return html;
  }

  formatReadable(days, hours, minutes, includeSeconds = false, seconds = 0) {
    const parts = [];
    
    if (days > 0) {
      parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
    }
    if (hours > 0) {
      parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    }
    if (minutes > 0 || (days === 0 && hours === 0)) {
      parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    }
    if (includeSeconds && seconds > 0) {
      parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);
    }
    
    if (parts.length === 0) {
      return '0 minutes';
    }
    
    if (parts.length === 1) {
      return parts[0];
    }
    
    if (parts.length === 2) {
      return `${parts[0]} and ${parts[1]}`;
    }
    
    // 3 or more parts
    const lastPart = parts.pop();
    return `${parts.join(', ')}, and ${lastPart}`;
  }

  shouldShowSeconds(days, hours) {
    if (this.countdownFormat === 'smart') {
      // Show seconds only when less than 24 hours remaining
      return days === 0 && hours < 24;
    }
    return false;
  }

  handleExpiration() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Hide countdown
    const countdownContainer = this.element.querySelector(`#Countdown-${this.sectionId}`) || 
                               this.element.querySelector(`#ProductCountdown-${this.sectionId}`) ||
                               this.element;
    if (countdownContainer) {
      countdownContainer.style.display = 'none';
    }

    // Update label text if it exists
    const labelElement = this.element.querySelector(`#Label-${this.sectionId}`);
    if (labelElement) {
      const availableTextHtml = labelElement.getAttribute('data-label-text-available-html');
      if (availableTextHtml) {
        labelElement.innerHTML = availableTextHtml;
      } else if (labelElement.dataset.labelTextAvailable) {
        labelElement.textContent = labelElement.dataset.labelTextAvailable;
      }
    }

    // For product pages, enable buy buttons and show price
    if (this.isProductPage) {
      this.enableProductPageFeatures();
    } else {
      // For banner pages, update button text
      if (this.buttonElement && this.buttonElement.dataset.buttonTextAvailable) {
        this.buttonElement.textContent = this.buttonElement.dataset.buttonTextAvailable;
      }

      // Show available text if it exists
      const availableText = this.element.querySelector('.countdown-banner__available');
      if (availableText) {
        availableText.style.display = 'block';
      }
    }
  }

  enableProductPageFeatures() {
    // Find and enable buy button
    const buyButton = document.querySelector(`#ProductSubmitButton-${this.sectionId}`) ||
                     document.querySelector('.embossable-product-form button[type="submit"]') ||
                     document.querySelector('.product-form__submit');
    
    if (buyButton) {
      buyButton.disabled = false;
      const buttonText = buyButton.querySelector('span');
      if (buttonText) {
        // Update button text to "Add to Cart" or keep current if it's already set
        if (buttonText.textContent.includes('Coming Soon') || buttonText.textContent.includes('coming soon')) {
          buttonText.textContent = 'Add to Cart';
        }
      }
    }

    // Show price if it was hidden
    const priceContainer = document.querySelector(`#price-${this.sectionId}`);
    if (priceContainer && (priceContainer.style.display === 'none' || priceContainer.dataset.comingSoonHidden === 'true')) {
      priceContainer.style.display = '';
      priceContainer.removeAttribute('data-coming-soon-hidden');
    }

    // Hide back-in-stock notification if showing
    const backInStock = document.querySelector('.back-in-stock-notification[style*="display: block"]');
    if (backInStock) {
      backInStock.style.display = 'none';
    }

    // Enable variant input if disabled
    const variantInput = document.querySelector('.product-variant-id');
    if (variantInput && variantInput.disabled) {
      variantInput.disabled = false;
    }

    // Trigger a custom event for other scripts to listen to
    document.dispatchEvent(new CustomEvent('product-countdown-expired', {
      detail: { sectionId: this.sectionId }
    }));
  }

  destroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

// Initialize all countdown banners and product countdowns on the page
function initProductCountdowns() {
  // Find banner countdowns
  const countdownBanners = document.querySelectorAll('[data-section-id][data-is-coming-soon="true"]');
  // Find product page countdowns
  const productCountdowns = document.querySelectorAll('[id^="ProductCountdown-"]');
  
  const countdowns = [];

  // Initialize banner countdowns
  countdownBanners.forEach((element) => {
    const hasCountdown = element.querySelector('[id^="Countdown-"]');
    if (hasCountdown) {
      try {
        const countdown = new ProductCountdown(element);
        if (countdown.countdownElement) {
          countdowns.push(countdown);
        }
      } catch (error) {
        console.error('Error initializing countdown banner:', error, element);
      }
    }
  });

  // Initialize product page countdowns
  productCountdowns.forEach((element) => {
    try {
      const countdown = new ProductCountdown(element);
      if (countdown.countdownElement) {
        countdowns.push(countdown);
      }
    } catch (error) {
      console.error('Error initializing product countdown:', error, element);
    }
  });

  return countdowns;
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductCountdowns);
} else {
  initProductCountdowns();
}

// Also initialize after a delay for dynamically loaded content
setTimeout(initProductCountdowns, 1000);
setTimeout(initProductCountdowns, 3000);
